import { Chess } from "chess.js";
import type { Move } from "../models/move-model";
import { first, invariant } from "@banjoanton/utils";
import type { MultiPvLine } from "../models/multi-pv-model";
import type { EvaluationMessageScore } from "../engines/evaluator-engine";

export const toComparableCp = (score: EvaluationMessageScore): number => {
    if (score.type === "cp") {
        return score.score;
    }

    if (score.score > 0) {
        return 100_000;
    }

    return -100_000;
};

const PIECE_CP: Record<string, number> = {
    p: 100,
    n: 300,
    b: 300,
    r: 500,
    q: 900,
    k: 10_000,
};

const captureValue = (type?: string) =>
    type ? PIECE_CP[type.toLowerCase()] : undefined;

const applyUciAndGetCapturedType = (
    fen: string,
    uci: string
): string | undefined => {
    const board = new Chess();
    board.load(fen);
    const move = board.move({
        from: uci.slice(0, 2),
        to: uci.slice(2, 4),
        promotion: "q",
    });
    return move?.captured;
};

const buildUci = (from?: string, to?: string) =>
    from && to ? `${from}${to}` : undefined;

function getUciFromMove(move: Move): string;
function getUciFromMove(move?: Move): string | undefined;
function getUciFromMove(move?: Move) {
    return move ? `${move.from}${move.to}` : undefined;
}

export type OnlyGoodMoveOpts = {
    equalityThresholdInCp: number;
    gapThresholdInCp: number;
};

type IsOnlyGoodMoveProps = {
    lines: MultiPvLine[];
    move: Move;
    options: OnlyGoodMoveOpts;
};

const isOnlyGoodMove = ({
    lines,
    move,
    options,
}: IsOnlyGoodMoveProps): boolean => {
    const { equalityThresholdInCp, gapThresholdInCp } = options;

    const best = lines.find((line) => line.rank === 1) ?? lines[0];
    const secondBest = lines.find((line) => line.rank === 2) ?? lines[1];

    invariant(best, "best line doesn't exist");
    invariant(secondBest, "second best line doesn't exist");

    const isWhiteMove = move.color === "w";

    const bestCp = best.rootScore.score;
    const secondBestCp = secondBest.rootScore.score;
    const gap = isWhiteMove ? bestCp - secondBestCp : secondBestCp - bestCp;

    const playedMove = getUciFromMove(move);
    const playedBestMove = first(best.moves) === playedMove;

    if (!playedBestMove) {
        return false;
    }

    const isEqualGameState =
        Math.abs(toComparableCp(best.rootScore)) < equalityThresholdInCp;

    const gapIsBelowThreshold = gap <= gapThresholdInCp;

    if (isEqualGameState || gapIsBelowThreshold) {
        return false;
    }
    return true;
};

const isOnlyLegalMove = (fenBeforeMove: string): boolean => {
    const board = new Chess();
    board.load(fenBeforeMove);
    if (!board.inCheck()) {
        return false;
    }
    return board.moves({ verbose: true }).length === 1;
};

const isKingEvasion = (fenBeforeMove: string, moveUci?: string): boolean => {
    if (!moveUci) {
        return false;
    }
    const board = new Chess();
    board.load(fenBeforeMove);
    const isCheck = board.inCheck();
    const move = board.move({
        from: moveUci.slice(0, 2),
        to: moveUci.slice(2, 4),
        promotion: "q",
    });
    const isKingMove = move.piece === "k";

    return isCheck && isKingMove;
};

const isEqualCapture = (params: {
    fenA: string;
    uciA: string;
    fenB: string;
    uciB: string;
}): boolean => {
    const { fenA, uciA, fenB, uciB } = params;

    const captureValueA = captureValue(applyUciAndGetCapturedType(fenA, uciA));
    const captureValueB = captureValue(applyUciAndGetCapturedType(fenB, uciB));

    return (
        captureValueA !== undefined &&
        captureValueB !== undefined &&
        captureValueA === captureValueB
    );
};

const isEvenMaterialTrade = (params: {
    fenBeforeOpponentsMove?: string;
    fenBeforeOurMove: string;
    currentFen: string;
    opponentsLastMoveUci?: string;
    moveUci?: string;
    opponentsNextMoveUci?: string;
}): boolean => {
    const {
        fenBeforeOpponentsMove,
        fenBeforeOurMove,
        currentFen,
        opponentsLastMoveUci,
        moveUci,
        opponentsNextMoveUci,
    } = params;

    if (
        fenBeforeOpponentsMove &&
        opponentsLastMoveUci &&
        moveUci &&
        isEqualCapture({
            fenA: fenBeforeOpponentsMove,
            uciA: opponentsLastMoveUci,
            fenB: fenBeforeOurMove,
            uciB: moveUci,
        })
    ) {
        return true;
    }

    if (
        moveUci &&
        opponentsNextMoveUci &&
        isEqualCapture({
            fenA: fenBeforeOurMove,
            uciA: moveUci,
            fenB: currentFen,
            uciB: opponentsNextMoveUci,
        })
    ) {
        return true;
    }

    return false;
};

const splitPvToMoves = (pv?: string) => {
    if (!pv) {
        return [];
    }

    const m = pv.match(/\bpv\b\s+(.+)$/i);
    const tail = m ? m[1] : pv;

    return tail!
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .filter((tok) => /^[a-h][1-8][a-h][1-8][nbrq]?$/i.test(tok));
};

export const MoveUtil = {
    isOnlyGoodMove,
    isOnlyLegalMove,
    isKingEvasion,
    isEvenMaterialTrade,
    buildUci,
    getUciFromMove,
    splitPvToMoves,
};
